/* ==========================================================================
   Oakwell Lane theme
   ========================================================================== */
(() => {
  const theme = window.theme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const define = (name, constructor) => {
    if (!customElements.get(name)) customElements.define(name, constructor);
  };

  /* Scroll lock and focus trapping
     ======================================================================== */
  let scrollLocks = 0;
  const lockScroll = () => {
    scrollLocks += 1;
    document.documentElement.classList.add('scroll-lock');
  };
  const unlockScroll = () => {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (scrollLocks === 0) document.documentElement.classList.remove('scroll-lock');
  };

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const trapFocus = (container, event) => {
    if (event.key !== 'Tab' || !container) return;
    const items = qsa(FOCUSABLE, container).filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /* Drawers
     ======================================================================== */
  class DrawerElement extends HTMLElement {
    constructor() {
      super();
      this.onKeydown = this.onKeydown.bind(this);
    }

    connectedCallback() {
      this.panel = this.querySelector('[role="dialog"]');
      this.addEventListener('click', (event) => {
        if (event.target.closest(this.closeSelector)) {
          event.preventDefault();
          this.close();
        }
      });
    }

    disconnectedCallback() {
      if (this.classList.contains('is-open')) unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
    }

    get closeSelector() {
      return '[data-close]';
    }

    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      document.addEventListener('keydown', this.onKeydown);
      requestAnimationFrame(() => this.panel && this.panel.focus({ preventScroll: true }));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      document.removeEventListener('keydown', this.onKeydown);
      if (this.trigger && typeof this.trigger.focus === 'function') this.trigger.focus({ preventScroll: true });
    }

    onKeydown(event) {
      if (event.key === 'Escape') this.close();
      else trapFocus(this.panel, event);
    }
  }

  class CartDrawer extends DrawerElement {
    get closeSelector() {
      return '[data-cart-close]';
    }
  }

  class MenuDrawer extends DrawerElement {
    get closeSelector() {
      return '[data-menu-close]';
    }

    open(trigger) {
      super.open(trigger);
      qsa('[data-menu-open]').forEach((el) => el.setAttribute('aria-expanded', 'true'));
    }

    close() {
      super.close();
      qsa('[data-menu-open]').forEach((el) => el.setAttribute('aria-expanded', 'false'));
    }
  }

  define('cart-drawer', CartDrawer);
  define('menu-drawer', MenuDrawer);

  /* Cart rendering (Section Rendering API)
     ======================================================================== */
  const Cart = {
    sectionIds() {
      return [...new Set(qsa('cart-items').map((el) => el.dataset.section).filter(Boolean))];
    },

    render(sections) {
      if (!sections) return;
      let count = null;
      Object.keys(sections).forEach((id) => {
        const html = sections[id];
        if (!html) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const source = doc.querySelector('cart-items');
        const target = qsa('cart-items').find((el) => el.dataset.section === id);
        if (!source || !target) return;
        target.innerHTML = source.innerHTML;
        target.className = source.className;
        target.dataset.cartCount = source.dataset.cartCount;
        count = parseInt(source.dataset.cartCount, 10);
      });
      if (count !== null && !Number.isNaN(count)) this.updateCount(count);
    },

    async refresh() {
      const ids = this.sectionIds();
      if (!ids.length) return;
      const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
      this.render(await response.json());
    },

    updateCount(count) {
      qsa('[data-cart-count-bubble]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('is-hidden', count === 0);
      });
    },

    bump() {
      qsa('.site-header__cart').forEach((el) => {
        el.classList.remove('is-bumping');
        void el.offsetWidth;
        el.classList.add('is-bumping');
      });
    },
  };

  theme.Cart = Cart;

  class CartItems extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.addEventListener('click', (event) => {
        const control = event.target.closest('[data-cart-qty]');
        if (!control) return;
        event.preventDefault();
        this.update(control.dataset.line, parseInt(control.dataset.cartQty, 10), control);
      });
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-cart-qty-input]');
        if (!input) return;
        this.update(input.dataset.line, Math.max(0, parseInt(input.value, 10) || 0), input);
      });
    }

    async update(line, quantity, origin) {
      if (this.busy) return;
      this.busy = true;
      this.classList.add('is-updating');

      const lineControls = qsa(`[data-line="${line}"]`, this);
      const focusIndex = origin ? lineControls.indexOf(origin) : -1;

      try {
        const response = await fetch(`${routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            line: parseInt(line, 10),
            quantity,
            sections: Cart.sectionIds(),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || strings.error);
        Cart.render(data.sections);
      } catch (error) {
        await Cart.refresh().catch(() => {});
        this.showError(error.message || strings.error);
      } finally {
        this.busy = false;
        this.classList.remove('is-updating');
        const next = focusIndex > -1 ? qsa(`[data-line="${line}"]`, this)[focusIndex] : null;
        if (next) next.focus({ preventScroll: true });
      }
    }

    showError(message) {
      const list = qs('.cart-items', this);
      if (!list) return;
      const note = document.createElement('p');
      note.className = 'form-error';
      note.setAttribute('role', 'alert');
      note.textContent = message;
      list.before(note);
      setTimeout(() => note.remove(), 5000);
    }
  }

  define('cart-items', CartItems);

  /* Product: gallery (swipe, drag, thumbnails, arrows, dots)
     ======================================================================== */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = qs('[data-gallery-viewport]', this);
      if (!this.viewport) return;
      this.slides = qsa('[data-slide]', this.viewport);
      this.thumbs = qsa('[data-thumb]', this);
      this.dots = qsa('[data-dot]', this);
      this.thumbScroller = qs('[data-gallery-thumbs-scroller]', this);
      this.index = parseInt(this.dataset.startIndex, 10) || 0;

      this.scrollToIndex(this.index, false);
      this.setActive(this.index, false);

      this.viewport.addEventListener(
        'scroll',
        () => {
          if (this.ticking) return;
          this.ticking = true;
          requestAnimationFrame(() => {
            this.ticking = false;
            this.onScroll();
          });
        },
        { passive: true }
      );
      this.viewport.addEventListener('scrollend', () => {
        this.lockUntil = 0;
        this.onScroll();
      });
      this.viewport.addEventListener('touchstart', () => {
        this.lockUntil = 0;
      }, { passive: true });

      this.addEventListener('click', this.onClick.bind(this), true);
      this.viewport.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.go(this.index + 1);
        } else if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.go(this.index - 1);
        }
      });

      this.setupDrag();

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.scrollToIndex(this.index, false));
        this.resizeObserver.observe(this.viewport);
      }
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    get count() {
      return this.slides.length;
    }

    scrollToIndex(index, smooth = true) {
      this.viewport.scrollTo({
        left: index * this.viewport.clientWidth,
        behavior: smooth && !reducedMotion ? 'smooth' : 'instant',
      });
    }

    onScroll() {
      if (this.dragging || performance.now() < (this.lockUntil || 0)) return;
      const width = this.viewport.clientWidth;
      if (!width) return;
      const index = Math.round(this.viewport.scrollLeft / width);
      if (index !== this.index) this.setActive(index);
    }

    setActive(index, scrollThumb = true) {
      this.index = index;
      this.slides.forEach((slide, i) => {
        slide.classList.toggle('is-active', i === index);
        const video = slide.querySelector('video');
        if (video) {
          if (i === index) video.play().catch(() => {});
          else video.pause();
        }
      });
      this.thumbs.forEach((thumb, i) => {
        const active = i === index;
        thumb.classList.toggle('is-active', active);
        if (active) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      if (scrollThumb) this.scrollThumbIntoView();
    }

    scrollThumbIntoView() {
      const thumb = this.thumbs[this.index];
      const scroller = this.thumbScroller;
      if (!thumb || !scroller || !scroller.clientHeight) return;
      const top = thumb.offsetTop - scroller.clientHeight / 2 + thumb.clientHeight / 2;
      scroller.scrollTo({ top, behavior: reducedMotion ? 'instant' : 'smooth' });
    }

    go(index) {
      const count = this.count;
      if (!count) return;
      const next = ((index % count) + count) % count;
      this.lockUntil = performance.now() + 800;
      this.setActive(next);
      this.scrollToIndex(next);
    }

    goToMedia(mediaId) {
      const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index > -1 && index !== this.index) this.go(index);
    }

    onClick(event) {
      if (this.dragMoved) {
        event.preventDefault();
        event.stopPropagation();
        this.dragMoved = false;
        return;
      }
      const nav = event.target.closest('[data-thumb], [data-dot]');
      if (nav) {
        this.go(parseInt(nav.dataset.index, 10));
        return;
      }
      if (event.target.closest('[data-gallery-prev]')) {
        this.go(this.index - 1);
        return;
      }
      if (event.target.closest('[data-gallery-next]')) {
        this.go(this.index + 1);
        return;
      }
      const zoom = event.target.closest('[data-zoom]');
      if (zoom) {
        const root = this.closest('product-info') || document;
        openLightbox(qs('[data-lightbox]', root), parseInt(zoom.dataset.index, 10));
      }
    }

    setupDrag() {
      if (this.count < 2) return;
      let startX = 0;
      let startLeft = 0;

      const onMove = (event) => {
        const delta = event.clientX - startX;
        if (!this.dragMoved && Math.abs(delta) > 6) {
          this.dragMoved = true;
          this.viewport.classList.add('is-dragging');
        }
        if (this.dragMoved) this.viewport.scrollLeft = startLeft - delta;
      };

      const onUp = (event) => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        this.dragging = false;
        if (!this.dragMoved) return;
        const delta = event.clientX - startX;
        const width = this.viewport.clientWidth;
        let target = Math.round(startLeft / width);
        if (delta < -50) target += 1;
        else if (delta > 50) target -= 1;
        target = Math.max(0, Math.min(this.count - 1, target));
        this.viewport.classList.remove('is-dragging');
        this.lockUntil = performance.now() + 800;
        this.setActive(target);
        this.scrollToIndex(target);
        setTimeout(() => {
          this.dragMoved = false;
        }, 0);
      };

      this.viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.dragging = true;
        this.dragMoved = false;
        startX = event.clientX;
        startLeft = this.viewport.scrollLeft;
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      });

      this.viewport.addEventListener('dragstart', (event) => event.preventDefault());
    }
  }

  define('product-gallery', ProductGallery);

  /* Product: zoom lightbox
     ======================================================================== */
  function openLightbox(dialog, index) {
    if (!dialog || typeof dialog.showModal !== 'function') return;
    const track = qs('[data-lightbox-track]', dialog);
    const slides = qsa('[data-lightbox-slide]', dialog);
    if (!track || !slides.length) return;

    const current = () => Math.round(track.scrollLeft / track.clientWidth);
    const goTo = (i) => {
      const next = Math.max(0, Math.min(slides.length - 1, i));
      track.scrollTo({ left: next * track.clientWidth, behavior: reducedMotion ? 'instant' : 'smooth' });
    };

    if (!dialog.dataset.ready) {
      dialog.dataset.ready = 'true';
      dialog.addEventListener('click', (event) => {
        if (event.target.closest('[data-lightbox-close]') || event.target === dialog || event.target.matches('[data-lightbox-slide]')) {
          dialog.close();
        } else if (event.target.closest('[data-lightbox-prev]')) {
          goTo(current() - 1);
        } else if (event.target.closest('[data-lightbox-next]')) {
          goTo(current() + 1);
        }
      });
      dialog.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') goTo(current() + 1);
        if (event.key === 'ArrowLeft') goTo(current() - 1);
      });
      dialog.addEventListener('close', unlockScroll);
    }

    slides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (img) img.loading = 'eager';
    });

    const position = Math.max(0, slides.findIndex((slide) => slide.dataset.index === String(index)));
    dialog.showModal();
    lockScroll();
    track.scrollTo({ left: position * track.clientWidth, behavior: 'instant' });
  }

  /* Product: variant selection
     ======================================================================== */
  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = qs('[data-product-json]', this);
      try {
        this.variants = json ? JSON.parse(json.textContent).variants : [];
      } catch (error) {
        this.variants = [];
      }
      this.productUrl = this.dataset.productUrl;
      this.addEventListener('change', (event) => {
        if (event.target.matches('[data-option-value]')) this.onOptionChange();
      });
    }

    get fieldsets() {
      return qsa('variant-picker fieldset', this);
    }

    selectedOptions() {
      return this.fieldsets.map((fieldset) => {
        const checked = qs('input:checked', fieldset);
        return checked ? checked.value : null;
      });
    }

    onOptionChange() {
      const selected = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((option, i) => option === selected[i]));

      this.updateLabels(selected);
      this.updateAvailability(selected);
      this.updateForm(variant);

      if (!variant) return;
      this.updatePrice(variant);
      if (this.productUrl) window.history.replaceState({}, '', `${this.productUrl}?variant=${variant.id}`);
      if (variant.featured_media_id) {
        const gallery = qs('product-gallery', this);
        if (gallery && gallery.goToMedia) gallery.goToMedia(variant.featured_media_id);
      }
    }

    updateLabels(selected) {
      this.fieldsets.forEach((fieldset, i) => {
        const output = qs('[data-selected-value]', fieldset);
        if (output && selected[i] !== null) output.textContent = selected[i];
      });
    }

    updateAvailability(selected) {
      this.fieldsets.forEach((fieldset, i) => {
        qsa('[data-option-value]', fieldset).forEach((input) => {
          const available = this.variants.some(
            (v) => v.available && v.options[i] === input.value && v.options.every((option, j) => j === i || option === selected[j])
          );
          const label = qs(`label[for="${input.id}"]`, fieldset);
          if (label) label.classList.toggle('is-unavailable', !available);
        });
      });
    }

    updateForm(variant) {
      const idInput = qs('[data-variant-id-input]', this);
      const button = qs('[data-add-to-cart]', this);
      const label = qs('[data-add-to-cart-label]', this);
      const error = qs('[data-product-error]', this);
      if (error) error.hidden = true;
      if (!button || !label) return;

      if (!variant) {
        button.disabled = true;
        label.textContent = strings.unavailable;
        return;
      }

      if (idInput) idInput.value = variant.id;
      button.disabled = !variant.available;
      label.textContent = variant.available ? strings.addToCart : strings.soldOut;
    }

    updatePrice(variant) {
      const container = qs('[data-product-price]', this);
      if (!container) return;
      const onSale = Boolean(variant.compare_at_price);
      container.innerHTML = `
        <div class="price price--large${onSale ? ' price--on-sale' : ''}">
          <span class="price__current">${variant.price}</span>
          ${onSale ? `<s class="price__compare">${variant.compare_at_price}</s>` : ''}
        </div>`;
    }
  }

  define('product-info', ProductInfo);

  /* Product: swatch hover previews the color name
     ======================================================================== */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      this.addEventListener('mouseover', (event) => {
        const swatch = event.target.closest('.swatch');
        if (!swatch) return;
        const output = qs('[data-selected-value]', swatch.closest('fieldset'));
        if (output) output.textContent = swatch.title;
      });
      this.addEventListener('mouseout', (event) => {
        const swatch = event.target.closest('.swatch');
        if (!swatch || swatch.contains(event.relatedTarget)) return;
        const fieldset = swatch.closest('fieldset');
        const checked = qs('input:checked', fieldset);
        const output = qs('[data-selected-value]', fieldset);
        if (output && checked) output.textContent = checked.value;
      });
    }
  }

  define('variant-picker', VariantPicker);

  /* Product: add to cart
     ======================================================================== */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = qs('form', this);
      this.button = qs('[data-add-to-cart]', this);
      this.error = qs('[data-product-error]', this);
      if (!this.form || !this.button) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-qty-step]');
        if (!step) return;
        const input = qs('input[name="quantity"]', this);
        if (!input) return;
        input.value = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(step.dataset.qtyStep, 10));
      });
    }

    async onSubmit(event) {
      event.preventDefault();
      const button = this.button;
      if (button.disabled || button.classList.contains('is-loading')) return;

      this.hideError();
      button.classList.remove('is-added');
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      const formData = new FormData(this.form);
      const sections = Cart.sectionIds();
      if (sections.length) {
        formData.append('sections', sections.join(','));
        formData.append('sections_url', window.location.pathname);
      }

      const started = performance.now();

      try {
        const response = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || strings.error);

        const elapsed = performance.now() - started;
        if (elapsed < 450) await wait(450 - elapsed);

        Cart.render(data.sections);
        button.classList.remove('is-loading');
        button.classList.add('is-added');
        Cart.bump();

        const drawer = qs('cart-drawer');
        if (theme.cartType === 'drawer' && drawer) {
          await wait(600);
          drawer.open(button);
          setTimeout(() => button.classList.remove('is-added'), 1400);
        } else {
          await wait(700);
          window.location.href = routes.cart;
        }
      } catch (error) {
        button.classList.remove('is-loading');
        this.showError(error.message || strings.error);
      } finally {
        button.removeAttribute('aria-busy');
      }
    }

    showError(message) {
      if (!this.error) return;
      this.error.textContent = message;
      this.error.hidden = false;
    }

    hideError() {
      if (this.error) this.error.hidden = true;
    }
  }

  define('product-form', ProductForm);

  /* Announcement bar
     ======================================================================== */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.messages = qsa('.announcement-bar__message', this);
      this.current = 0;
      if (this.messages.length < 2) return;
      this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
    }

    disconnectedCallback() {
      this.stop();
    }

    start() {
      this.stop();
      this.timer = setInterval(() => this.show(this.current + 1), parseInt(this.dataset.interval, 10) || 5000);
    }

    stop() {
      clearInterval(this.timer);
    }

    show(index) {
      this.messages[this.current].classList.remove('is-active');
      this.current = (index + this.messages.length) % this.messages.length;
      this.messages[this.current].classList.add('is-active');
    }
  }

  define('announcement-bar', AnnouncementBar);

  document.addEventListener('shopify:block:select', (event) => {
    const bar = event.target.closest('announcement-bar');
    if (!bar || !bar.messages) return;
    const index = bar.messages.indexOf(event.target);
    if (index > -1) {
      bar.stop();
      bar.show(index);
    }
  });

  /* Global interactions
     ======================================================================== */
  document.addEventListener('click', (event) => {
    const cartTrigger = event.target.closest('[data-cart-open]');
    if (cartTrigger) {
      const drawer = qs('cart-drawer');
      if (drawer && theme.cartType === 'drawer') {
        event.preventDefault();
        drawer.open(cartTrigger);
      }
      return;
    }

    const menuTrigger = event.target.closest('[data-menu-open]');
    if (menuTrigger) {
      const menu = qs('menu-drawer');
      if (menu) {
        event.preventDefault();
        menu.open(menuTrigger);
      }
    }
  });

  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-auto-submit]');
    if (select && select.form) select.form.submit();
  });

  /* Tactile buttons: ripple from the press point, and a pressed state for keyboard users */
  document.addEventListener('pointerdown', (event) => {
    const button = event.target.closest('.button--tactile');
    if (!button || button.disabled || reducedMotion) return;
    const rect = button.getBoundingClientRect();
    const ripple = document.createElement('span');
    ripple.className = 'ripple';
    ripple.style.left = `${event.clientX - rect.left}px`;
    ripple.style.top = `${event.clientY - rect.top}px`;
    button.appendChild(ripple);
    ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const button = event.target.closest && event.target.closest('.button--tactile');
    if (button && !button.disabled) button.classList.add('is-pressed');
  });

  document.addEventListener('keyup', () => {
    qsa('.button--tactile.is-pressed').forEach((button) => button.classList.remove('is-pressed'));
  });

  /* Header height, used for sticky offsets */
  const header = qs('.section-header');
  if (header) {
    const setHeaderHeight = () =>
      document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
    setHeaderHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeaderHeight).observe(header);
  }

  /* Scroll reveal */
  const designMode = window.Shopify && window.Shopify.designMode;
  if (!reducedMotion && !designMode && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('reveal-ready');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.1 }
    );
    qsa('[data-reveal]').forEach((el) => observer.observe(el));
  }
})();
